import { SvgIcon } from "@mui/material";
import type React from "react";
import { type Dispatch, type SetStateAction, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import type { PopoverPosition } from "react-tiny-popover";
import { findMatchingSortOption, type MultiSortOption, type SortingState } from "../../../../utils/multiColumnSorting";
import { ToolbarTriggerButton } from "../../Clickable/ToolbarTriggerButton";
import { SystemReactTinyPopover, type SystemReactTinyPopoverProps } from "../SystemReactTinyPopover";
import { SystemSortingOptionsContent } from "./SystemSortingOptionsContent";

// Re-export so existing imports of `MultiSortOption` from this file keep working.
export type { MultiSortOption };

export type SystemSortingProps<T> = {
	/**
	 * Options for sorting - label and which columns to sort by
	 */
	sortingOptions: MultiSortOption<T>[];
	/**
	 * Optional key to save the sort state in local storage
	 */
	localStorageTableKey?: string;
	/**
	 * Initial state for the sorting - must provide at least one column to sort by
	 */
	initialState: SortingState<T>[];
	/**
	 * Function to update the sorting state in the parent component
	 */
	setSortState: Dispatch<SetStateAction<SortingState<T>[]>>;
	/**
	 * Optional sorting state to be used when restoring default click - if not provided, the initial state will be used
	 */
	defaultSortState?: SortingState<T>[];

	/**
	 * Accessible name (and tooltip) for the trigger, already localized. The button now carries a
	 * visible "Sort" label, so this is only worth passing when the control sorts something specific
	 * enough that the generic name would be ambiguous.
	 */
	triggerAriaLabel?: string;

	/**
	 * Fewest sort rows the user may leave in the list. Once the list is down to this many, their
	 * clear buttons disappear, so the popup cannot emit a shorter state than the caller can accept.
	 *
	 * Omitted means NO floor, which is the right answer for a data grid: "no sort at all" is a
	 * legitimate state there, and every caller predating this prop relies on being able to reach it.
	 * Pass `1` when the consumer has no meaning for an empty sort — the staffing monthly export's
	 * `sortEntries` is `min_length=1` server-side, so an empty list there is a 422 rather than a
	 * sensible request.
	 */
	minEntries?: number;

	/**
	 * @deprecated This will be removed in a future release. Use `overridePopoverProps.positions` instead.
	 */
	popupPositions?: PopoverPosition[] | PopoverPosition;

	/**
	 * Optional props to override the default popover behavior and style. This button always
	 * drives the popover in controlled mode (`isOpen` above) — `isOpen`/`enterDelay`/`leaveDelay`/`onOpenChange`
	 * are intentionally excluded here so an override can never fight that or slip into hover mode,
	 * and `onClose` is excluded so an override can't replace the handler that clears `sortMenuOpen`
	 * (which would leave the controlled popover stuck open after an outside click).
	 */
	overridePopoverProps?: Partial<Omit<SystemReactTinyPopoverProps, "isOpen" | "enterDelay" | "leaveDelay" | "onOpenChange" | "onClose">>;

	/**
	 * Drop the "Sort" label and render the trigger as a square icon glyph. The escape hatch for a
	 * toolbar with genuinely no room for the word — not the default, since an unlabelled sort glyph
	 * is the defect this button exists to fix.
	 */
	iconOnly?: boolean;
	/**
	 * Test id for the trigger button. Left off by default rather than hardcoded: this is a shared
	 * component, and a fixed id turns a page with two of these into a strict-mode locator violation.
	 */
	dataTestId?: string;
};

/**
 * Descending bars plus a direction arrow.
 *
 * MUI's `Sort` glyph is bars only, which reads as "list" or "filter" as often as it reads as "sort"
 * — and an unlabelled version of it floating above a table was one of the measured defects on the
 * Team page. The arrow is what makes the control legible at a glance.
 */
const SortBarsIcon = () => (
	<SvgIcon viewBox="0 0 24 24" sx={{ fontSize: 18 }}>
		<path d="M4 7h13M4 12h9M4 17h5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
		<path d="M15 14l3 3 3-3M18 17V6" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
	</SvgIcon>
);

export interface SortingButtonHandle {
	openMenu: () => void;
	closeMenu: () => void;
}

export function SystemSortingButtonWithPopup<T>({
	ref,
	// DEFAULT: labelled. The icon-only shape was kept for the hand-rolled toolbars so they were not
	// resized under them, but it carries the very defect this button was built to fix — people do not
	// find an unlabelled glyph, and cannot tell it from a filter. The word is the control.
	iconOnly = false,
	dataTestId,
	...props
}: SystemSortingProps<T> & { ref?: React.Ref<SortingButtonHandle> }) {
	const { t } = useTranslation();
	const [sortMenuOpen, setSortMenuOpen] = useState(false);

	useImperativeHandle(ref, () => ({
		openMenu: () => setSortMenuOpen(true),
		closeMenu: () => setSortMenuOpen(false),
	}));

	// Only a multi-key sort gets a badge. Nearly every grid carries a one-key default sort, so
	// badging that would put a permanent "1" on every toolbar and mean nothing. More than one key is
	// the state people can't otherwise see — and the state they end up in by accident.
	const sortKeyCount = props.initialState.filter((entry) => findMatchingSortOption(entry.sortColumns, props.sortingOptions)).length;
	const label = t("sort.button.label");
	// `num`, not `count` — `count` triggers i18next plural-suffix resolution, which these flat keys don't carry.
	const description = props.triggerAriaLabel ?? (sortKeyCount > 1 ? t("sort.button.tooltip.multi", { num: sortKeyCount }) : label);

	return (
		<SystemReactTinyPopover
			positions={props.popupPositions ?? ["right", "bottom", "top", "left"]}
			isOpen={sortMenuOpen}
			onClose={() => setSortMenuOpen(false)}
			content={<SystemSortingOptionsContent {...props} />}
			arrowOpacity={1}
			{...props.overridePopoverProps}
		>
			<ToolbarTriggerButton
				label={label}
				description={description}
				icon={<SortBarsIcon />}
				count={sortKeyCount > 1 ? sortKeyCount : undefined}
				onClick={() => setSortMenuOpen((prev) => !prev)}
				iconOnly={iconOnly}
				dataTestId={dataTestId}
			/>
		</SystemReactTinyPopover>
	);
}
